import { AsyncPipe, NgFor, NgIf } from "@angular/common";
import { Component, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatTooltipModule } from "@angular/material/tooltip";
import { TranslocoPipe, TranslocoService } from "@ngneat/transloco";
import { map, Observable, startWith, Subject, takeUntil } from "rxjs";
import { AppConfigService } from "app/services/app-config.service";
import { OrganizationService } from "app/core/auth/organization.service";
import { IOrganization } from "app/modules/admin/organizations/model/IOrganization";
import { OrganizationsService } from "app/modules/admin/organizations/services/organizations.service";

/** Option type: null = default context ("All organizations" / own organization), otherwise the selected org */
type OrganizationOption = IOrganization | null;

/**
 * Organization context switcher (X-Organization-ID).
 *
 * Shown to whoever the backend flags with `can_impersonate` in the
 * App-Configuration header: super-admins ("All organizations" as default) and
 * owners (role `owner`) of an allowlisted organization (APPCELERATE) who may
 * jump to any organization without the rest of super-admin, with their own
 * organization as default. The list itself comes from
 * `GET /organization/impersonable`, so the SPA never decides the allowed set.
 *
 * The selection lives in localStorage (see AppConfigService) and is read by
 * the auth interceptor, theming, branding and a few filters. Whenever the
 * backend stops honouring it (owner demoted, organization dropped from the
 * allowlist, organization gone) it is dropped here and the page reloaded, so
 * the SPA never stays half-switched to an organization the API ignores.
 */
@Component({
    selector: "organization-selector",
    templateUrl: "./organization-selector.component.html",
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        NgIf,
        NgFor,
        AsyncPipe,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatAutocompleteModule,
        MatIconModule,
        MatTooltipModule,
        TranslocoPipe,
    ],
})
export class OrganizationSelectorComponent implements OnInit, OnDestroy {
    /** Selector offered at all (super-admin or allowlisted owner). */
    canImpersonate = false;
    /** Super-admin: unrestricted, "All organizations" default. */
    isSuperAdmin = false;
    organizations: IOrganization[] = [];
    searchControl = new FormControl<OrganizationOption | string>(null);
    filteredOptions: Observable<OrganizationOption[]>;

    private _unsubscribeAll = new Subject<void>();
    private _organizationsLoadStarted = false;
    /** The user's own organization (localStorage user), read once. */
    private readonly _homeOrganizationId: string | null;
    private readonly _homeOrganizationName: string | null;

    constructor(
        private _appConfigService: AppConfigService,
        private _organizationsService: OrganizationsService,
        private _organizationService: OrganizationService,
        private _transloco: TranslocoService,
    ) {
        const home = this._organizationService.getOrganization();
        this._homeOrganizationId = home?.id ?? null;
        this._homeOrganizationName = home?.name ?? null;

        this.filteredOptions = this.searchControl.valueChanges.pipe(
            startWith(""),
            map((value) => {
                const search = typeof value === "string" ? value : (value as IOrganization)?.name ?? "";
                return this._filter(search);
            })
        );
    }

    ngOnInit(): void {
        this._applyConfig();
        this._appConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => this._applyConfig());
        this._transloco.langChanges$.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
            const current = this.searchControl.value;
            this.searchControl.setValue(current, { emitEvent: false });
        });
    }

    /** Tooltip key: the super-admin wording mentions the unrestricted context. */
    get tooltipKey(): string {
        return this.isSuperAdmin
            ? "organization.selector.context_tooltip"
            : "organization.selector.context_tooltip_delegated";
    }

    /** Detect super-admin from AppConfig (response header) or from user in localStorage so selector shows on first paint. */
    private _isSuperAdminFromStorage(): boolean {
        return this._organizationService.getUser()?.role === "super-admin";
    }

    private _applyConfig(): void {
        // canImpersonate() already covers the super-admin role once the header
        // arrived; the localStorage fallback only serves the first paint.
        this.isSuperAdmin =
            this._appConfigService.hasRole("super-admin") || this._isSuperAdminFromStorage();
        this.canImpersonate = this.isSuperAdmin || this._appConfigService.canImpersonate();

        // The header arrived and says this user may not switch (owner demoted,
        // organization dropped from the allowlist...) while a selection is
        // still stored: the API already ignores that header, drop it so the
        // rest of the SPA stops acting as that organization.
        if (
            this._appConfigService.isLoaded() &&
            !this.canImpersonate &&
            this._appConfigService.getSelectedOrganizationId()
        ) {
            this._dropStoredSelection();
            return;
        }

        if (this.canImpersonate && !this._organizationsLoadStarted) {
            this._organizationsLoadStarted = true;
            this._organizationsService
                .impersonable()
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe((list: IOrganization[]) => {
                    // For an owner the default (null) option already stands for
                    // their own organization: don't list it twice.
                    this.organizations = (list || []).filter(
                        (org) => this.isSuperAdmin || org.id !== this._homeOrganizationId
                    );
                    this._setControlFromStoredSelection();
                });
        }
    }

    private _setControlFromStoredSelection(): void {
        const id = this._appConfigService.getSelectedOrganizationId();
        if (!id) {
            this.searchControl.setValue(null, { emitEvent: false });
            return;
        }

        const org = this.organizations.find((o) => o.id === id);

        if (!org && !this.isSuperAdmin) {
            // Stored selection no longer switchable (organization gone, own
            // organization...): same treatment as above.
            this._dropStoredSelection();
            return;
        }

        this.searchControl.setValue(org ?? null, { emitEvent: false });
        if (org?.name && !this._appConfigService.getSelectedOrganizationName()) {
            this._appConfigService.setSelectedOrganization(id, org.name);
        }
    }

    /** Forget the stored organization and reload: the page was painted (theme, filters) as that organization. */
    private _dropStoredSelection(): void {
        this._appConfigService.setSelectedOrganization(null, null);
        this.searchControl.setValue(null, { emitEvent: false });
        window.location.reload();
    }

    private _filter(searchValue: string): OrganizationOption[] {
        const filterValue = searchValue.toLowerCase().trim();
        const allOrgs: OrganizationOption[] = [null];
        if (!filterValue) {
            return allOrgs.concat(this.organizations);
        }
        const matched = this.organizations.filter((org) =>
            (org.name ?? "").toLowerCase().includes(filterValue)
        );
        return allOrgs.concat(matched);
    }

    /** Label of the default (null) option: "All organizations" for super-admins, own organization otherwise. */
    defaultOptionLabel(): string {
        if (this.isSuperAdmin) {
            return this._transloco.translate("organization.selector.all");
        }
        return this._homeOrganizationName || this._transloco.translate("organization.selector.own");
    }

    displayOrganization = (org: OrganizationOption | string): string => {
        if (typeof org === "string") return org;
        if (org === null) return this.defaultOptionLabel();
        return org?.name ?? "";
    };

    onInputClick(): void {
        const value = this.searchControl.value;
        if (typeof value === "string") {
            this.searchControl.setValue("");
        }
        this.searchControl.updateValueAndValidity();
    }

    onOptionSelected(org: OrganizationOption): void {
        this.searchControl.setValue(org);
        const id = org?.id ?? null;
        const name = org?.name ?? null;
        this._appConfigService.setSelectedOrganization(id, name);
        window.location.reload();
    }

    clearOrganizationSelection(): void {
        this.searchControl.setValue(null);
        this._appConfigService.setSelectedOrganization(null, null);
        window.location.reload();
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }
}
